import React from 'react';
import dataType from '../../../infrastructure/constants/dataType';
import { mapToBasicOption } from '../../../infrastructure/helpers/html/selectOptionHelper';
import ColumnDefinition from '../../../infrastructure/types/DataGrid/ColumnDefinition';
import Assignment from '../../../types/functions/Assignment/Assignment';
import UserDefinedFunction from '../../../types/report/UserDefinedFunction';
import CustomIcon from '../../Common/BootstrapIcons/Custom/CustomIcon';
import iconTooltip from '../../Common/BootstrapIcons/Tooltip/IconTooltip';
import { ReactComponent as RemoveIcon } from '../../Common/BootstrapIcons/close.svg';
import EditableText from '../../Common/EditableText/EditableText';
import SelectList from '../../Common/SelectList/SelectList';
import Mode from '../../../types/report/Mode';
import { ReactComponent as ViewIcon } from '../../Common/BootstrapIcons/view.svg';
import SelectOption from '../../../infrastructure/types/SelectOption';
import ComponentType from '../../../types/report/ComponentType';

const dataTypeOptions = Object.values(dataType).map(mapToBasicOption);

const getDataTypeContent = (func: UserDefinedFunction, onChange: (e: React.ChangeEvent<HTMLElement>, type: string, number: number, assignment: Assignment) => void, isReadOnly: boolean, onLockSectionClick?: (number: number) => Promise<boolean>): JSX.Element => {

    const onChangeCheckIfLocked = async (type: string, number: number, assignment: Assignment, event: React.ChangeEvent<HTMLElement>): Promise<void> => {

        const isLocked = onLockSectionClick && await onLockSectionClick(number);
        if (!isLocked) {
            onChange(event, type, func.number, func.assignment);
        }
        else {
            event.preventDefault();
        }
    }

    return (
        <SelectList
            className="inline-select"
            options={dataTypeOptions}
            value={func.returnType}
            onClick={async (event) => onLockSectionClick && await onLockSectionClick(func.number)}
            onChange={(type, event: React.ChangeEvent<HTMLElement>) => { onLockSectionClick ? onChangeCheckIfLocked(type, func.number, func.assignment, event) : onChange(event, type, func.number, func.assignment) }}
            disabled={isReadOnly} />
    );
};


const getFunctionMode = (udf: UserDefinedFunction, onGlobalFunctionLinkClick: (globalFunctionId: number, udfId: number) => void): JSX.Element => {

    return <div className='display-flex'>
        <span>{udf.mode}</span>
        {udf.mode === Mode.Global &&
            <button
                className="custom-icon-button"
                onClick={(event) => {
                    onGlobalFunctionLinkClick(udf.globalUDFModuleId || 0, udf.globalUDFModuleId as number)
                }}
            >
                <CustomIcon className='main-blue-color icon-small' icon={ViewIcon} title={iconTooltip.view} />
            </button>
        }
    </div>
}

const getColumns = (
    onNameChange: (name: string, number: number) => void,
    onReturnTypeChange: (e: React.ChangeEvent<HTMLElement>, type: string, number: number, assignment: Assignment) => void,
    onBusinessDescriptionChange: (businessDescription: string, number: number) => void,
    onRemoveClick: (number: number) => void,
    isReadOnly: boolean,
    onGlobalFunctionLinkClick: (functionid: number) => void,
    onUdfReferenceSelected: (componentType: ComponentType, referencedFunctionNumber: number, value: string, text: string, innerType: string, configId?: number, mainComponentNumber?: number) => void,
    getUdfReferences: (number: number) => SelectOption[],
): ColumnDefinition<UserDefinedFunction>[] => {

    let columns: ColumnDefinition<UserDefinedFunction>[] = [
        {
            header: 'Reference',
            getContent: x => x.number.toString(),
            width: '70px'
        },
        {
            header: 'Function Name',
            getContent: x => <EditableText disabled={isReadOnly} value={x.name} onChange={value => onNameChange(value, x.number)} multiline={true} />,
            width: '150px'
        },
        {
            header: 'Mode',
            getContent: f => getFunctionMode(f, onGlobalFunctionLinkClick),
            width: '50px'
        },
        {
            header: 'Return Type',
            getContent: x => getDataTypeContent(x, onReturnTypeChange, isReadOnly),
            width: '100px'
        },
        {
            header: 'Business Description',
            getContent: x => <EditableText disabled={isReadOnly} value={x.businessDescription} onChange={value => onBusinessDescriptionChange(value, x.number)} multiline={true} style={{ width: '100%', height: '60px' }} />,
            width: '200px'
        },
        {
            header: '',
            getContent: x =>
                <div className='display-flex align-items-start justify-content-between'>
                    <SelectList
                        options={[{ id: x.id, value: 'References', text: 'References' } as SelectOption].concat(getUdfReferences(x.number))}
                        value={x.name}
                        onChange={(value, event, text, number, componentType, innerType, configId, mainComponentNumber) => {
                            onUdfReferenceSelected(componentType || ComponentType.None, number || 0, value, text, innerType || '', configId, mainComponentNumber || 0)
                        }}
                        className='references-dropdown border-none'
                        onClick={() => getUdfReferences(x.number)}
                    />
                    <button
                        className="custom-icon-button float-right"
                        onClick={() => onRemoveClick(x.number)}>
                        <CustomIcon icon={RemoveIcon} title={iconTooltip.remove} />
                    </button>
                </div>,
            width: '100px'
        }
    ];

    return columns;
};

export { getColumns };
